import { Fragment, useMemo } from "react";
import { useIntl } from "react-intl";
import {
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
    getPaginationRowModel,
} from "@tanstack/react-table";
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TablePagination,
    Stack,
    IconButton
} from "@mui/material";
import { ANCHO_MINIMO_PROVEEDORES, construirColumnasProveedores } from "./CotizacionProveedoresColumnas";
import { CotizacionProductosSubtabla } from "./CotizacionProductosSubtabla";
import type { FilaProductoCotizado, FilaProveedorCotizado } from "../types/cotizacionProveedores.types";
import { SeleccionProductos } from "../utils/cotizacionSeleccion.utils";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

interface MetaColumna {
    ancho?: number;
    alineacion?: "left" | "center" | "right";
}

interface CotizacionProveedoresTablaProps {
    filas: FilaProveedorCotizado[];
    mensajeVacio?: string;
    esComputo: boolean;
    seleccion: SeleccionProductos;
    inventariosAutorizados: Set<number>;
    onToggleProducto: (p: FilaProductoCotizado) => void;
    motivoBloqueoCotizacion?: string | null;
    guardadosPorInventario: Record<number, string>;
}

export const CotizacionProveedoresTabla = ({
    filas,
    mensajeVacio,
    esComputo,
    seleccion,
    inventariosAutorizados,
    onToggleProducto,
    motivoBloqueoCotizacion = null,
    guardadosPorInventario
}: CotizacionProveedoresTablaProps): JSX.Element => {
    const intl = useIntl();

    const textos = useMemo(
        () => ({
            proveedor: intl.formatMessage({ id: "cotizacion_proveedor", defaultMessage: "Proveedor" }),
            contacto: intl.formatMessage({ id: "cotizacion_contacto", defaultMessage: "Contacto" }),
            correo: intl.formatMessage({ id: "cotizacion_correo", defaultMessage: "Correo" }),
            rfc: intl.formatMessage({ id: "cotizacion_rfc", defaultMessage: "RFC" }),
            telefono: intl.formatMessage({ id: "cotizacion_telefono", defaultMessage: "Teléfono" }),
            productos: intl.formatMessage({ id: "cotizacion_productos", defaultMessage: "Productos" }),
            total: intl.formatMessage({ id: "cotizacion_total", defaultMessage: "Total" }),
            respondio: intl.formatMessage({ id: "cotizacion_respondio", defaultMessage: "¿Respondió?" }),
            si: intl.formatMessage({ id: "general_si", defaultMessage: "SI" }),
            no: intl.formatMessage({ id: "general_no", defaultMessage: "NO" }),
            verProductos: intl.formatMessage({ id: "cotizacion_ver_productos", defaultMessage: "Ver productos" }),
        }),
        [intl]
    );

    const columnas = useMemo(() => construirColumnasProveedores(textos), [textos]);

    const tabla = useReactTable({
        data: filas,
        columns: columnas,
        getRowId: (fila) => fila.id,
        getRowCanExpand: (fila) => fila.original.productos.length > 0,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 5 } },
        autoResetPageIndex: false,
    });

    const meta = (columnDef: unknown): MetaColumna =>
        ((columnDef as { meta?: MetaColumna }).meta ?? {});

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3 }}>
            <TableContainer>
                <Table
                    size="small"
                    sx={{
                        display: "table",
                        width: "100%",
                        minWidth: ANCHO_MINIMO_PROVEEDORES,
                        tableLayout: "fixed",
                    }}
                >
                    <colgroup>
                        {tabla.getVisibleLeafColumns().map((col) => (
                            <col key={col.id} style={{ width: meta(col.columnDef).ancho }} />
                        ))}
                    </colgroup>
                    <TableHead sx={{ background: "#041d45", display: "table-header-group" }}>
                        {tabla.getHeaderGroups().map((grupo) => (
                            <TableRow key={grupo.id} sx={{ display: "table-row" }}>
                                {grupo.headers.map((h) => (
                                    <TableCell
                                        key={h.id}
                                        align={meta(h.column.columnDef).alineacion ?? "left"}
                                        sx={{
                                            display: "table-cell",
                                            fontWeight: 600,
                                            fontSize: 14,
                                            color: "#fff",
                                            px: h.column.id === "expandir" ? 0.5 : undefined,
                                        }}
                                    >
                                        {h.isPlaceholder
                                            ? null
                                            : flexRender(h.column.columnDef.header, h.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody sx={{ display: "table-row-group" }}>
                        {tabla.getRowModel().rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columnas.length} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {mensajeVacio ??
                                            intl.formatMessage({ id: "general_sin_registros" })}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {tabla.getRowModel().rows.map((fila) => (
                            <Fragment key={fila.id}>
                                <TableRow hover sx={{ display: "table-row" }}>
                                    {fila.getVisibleCells().map((celda) => (
                                        <TableCell
                                            key={celda.id}
                                            align={meta(celda.column.columnDef).alineacion ?? "left"}
                                            sx={{
                                                display: "table-cell",
                                                px: celda.column.id === "expandir" ? 0.5 : undefined,
                                            }}
                                        >
                                            {flexRender(celda.column.columnDef.cell, celda.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>

                                {fila.getIsExpanded() && (
                                    <TableRow sx={{ display: "table-row" }}>
                                        <TableCell colSpan={columnas.length} sx={{ display: "table-cell", p: 0 }}>
                                            <CotizacionProductosSubtabla
                                                productos={fila.original.productos}
                                                esComputo={esComputo}
                                                seleccion={seleccion}
                                                inventariosAutorizados={inventariosAutorizados}
                                                onToggle={onToggleProducto}
                                                motivoBloqueoCotizacion={motivoBloqueoCotizacion}
                                                guardadosPorInventario={guardadosPorInventario}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
                {filas.length > 5 && (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ p: 1 }}>
                        <Typography variant="caption">
                            Página {tabla.getState().pagination.pageIndex + 1} de {tabla.getPageCount()}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => { tabla.resetExpanded(); tabla.previousPage(); }}
                            disabled={!tabla.getCanPreviousPage()}
                        >
                            <IconChevronLeft size={20} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => { tabla.resetExpanded(); tabla.nextPage(); }}
                            disabled={!tabla.getCanNextPage()}
                        >
                            <IconChevronRight size={20} />
                        </IconButton>
                    </Stack>
                )}
            </TableContainer>
        </Paper>
    );
};